/**
 *
 * This code defines a JavaScript component for managing the behavior of a website's header. It includes functionality for handling announcements, scrolling behavior, transparency, menu state, and responsive design.
 * Key features include:
 * 1. Announcement Management: Controls the display and closure of announcements, adjusting the header height accordingly.
 * 2. Scroll Handling: Manages header visibility based on scroll direction and position
 * 3. Transparency: Enables transparent header functionality based on scroll position.
 * 4. Responsive Design: Adjusts header behavior for mobile and dropdown navigation states.
 * 5. Initialization: Sets up event listeners for scrolling and resizing, initializes component state, and handles mobile navigation.
 */
function headerComponent() {
  return {
    isAnnouncement: true,
    lastScrollTop: 0,
    transparentHeaderEnabled: false,
    isTransparent: false,
    isMenuOpen: false,
    initialAnnouncementBarHeight: this.$el.querySelector('.promotional-banner')?.offsetHeight || 0,
    pageNavHeight: this.$el.querySelector('.page-navigation-wrapper')?.offsetHeight || 0,
    navbarHeight: this.$el.offsetHeight - this.pageNavHeight,
    throttleTimer: false,

    announcementClose() {
      sessionStorage.setItem('announcement', false);
      this.isAnnouncement = false;
      this.navbarHeight = this.navbarHeight - this.initialAnnouncementBarHeight;
      this.setHtmlPadding();
    },

    eventThrottle(callback) {
      if (this.throttleTimer) return;
      this.throttleTimer = true;
      requestAnimationFrame(() => {
        callback();
        this.throttleTimer = false;
      });
    },

    handleScroll() {
      if (this.isMenuOpen) return;

      let currentScroll = window.scrollY || document.documentElement.scrollTop;

      this.handleIsTransparent();

      if (currentScroll > this.lastScrollTop && currentScroll > this.navbarHeight) {
        this.$el.style.top = `-${this.navbarHeight}px`;
      } else {
        this.$el.style.top = '0px';
      }

      this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
    },

    handleIsTransparent() {
      let currentScroll = window.scrollY || document.documentElement.scrollTop;
      this.isTransparent = this.transparentHeaderEnabled && !this.isMenuOpen && currentScroll < 3;
    },

    setTransparentBanner() {
      const heroBannerSettingsEl = document.querySelector('#hero-banner-settings');
      if (heroBannerSettingsEl) {
        const settings = JSON.parse(heroBannerSettingsEl.textContent);
        if (settings.enable_transparent_header) {
          this.transparentHeaderEnabled = true;
        } else {
          this.transparentHeaderEnabled = false;
        }
      }
    },

    recalculateHeight() {
      this.pageNavHeight = this.$el.querySelector('.page-navigation-wrapper')?.offsetHeight || 0;
      this.navbarHeight = this.$el.offsetHeight - this.pageNavHeight;
      this.setHtmlPadding();
    },

    setHtmlPadding() {
      const bannerHeight = this.$el.querySelector('.promotional-banner')?.offsetHeight || 0;
      const topPadding = this.transparentHeaderEnabled ? bannerHeight : this.navbarHeight;
      document.body.style.paddingTop = `${topPadding}px`;
    },

    isAnnouncementBarClosed() {
      const announcementClosed = window.sessionStorage.getItem('announcement');
      if (announcementClosed == 'false') {
        this.isAnnouncement = false;
      }
      this.$nextTick(() => {
        this.recalculateHeight();
      });
    },

    init() {
      this.setTransparentBanner();
      window.addEventListener('scroll', () => this.eventThrottle(() => this.handleScroll()));
      window.addEventListener('resize', () => this.eventThrottle(() => this.recalculateHeight()));

      this.handleScroll();
      this.$watch('$store.nav.isMobileNavOpen', (newVal) => {
        this.handleNavStateChange(newVal, this.$store.nav.dropdownActive);
      });
      this.$watch('$store.nav.dropdownActive', (newVal) => {
        this.handleNavStateChange(this.$store.nav.isMobileNavOpen, newVal);
      });
      this.isAnnouncementBarClosed();
      this.$el.style.visibility = 'visible';
    },

    handleNavStateChange(isMobileNavOpen, dropdownActive) {
      this.isMenuOpen = isMobileNavOpen || dropdownActive;
      this.handleIsTransparent();
      if (dropdownActive && !this.isAnnouncement && this.pageNavHeight > 0) {
        const submenus = this.$el.querySelectorAll('.navigation__submenu');
        submenus.forEach(function (submenu) {
          submenu.style.top = '60px';
        });
      }
      if (isMobileNavOpen) {
        document.querySelector('.anatta-mobile-nav-drawer').querySelector('.drawer__drawer').style.top =
          `${this.navbarHeight}px`;
      }
    },
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('headerComponent', headerComponent);
});
